// The code map of the open folder: read in the parse worker from every C# file a person wrote
// (generated files, build output and hidden folders are left out) and the project files (to tell
// test projects), and read again when files change.
import { isGeneratedCode, isMappedPath, type CodeMap } from '@modelviewer/core';
import { create } from 'zustand';
import { parser } from '../workers/parseClient';
import { setExtraWatchFolders, useWorkspace } from './workspace';

/** The tab showing the code: the code map, the endpoint map, or the test map. */
export type CodeView = 'code' | 'endpoints' | 'tests';

export type CodeSelection =
  | { kind: 'file'; path: string }
  | { kind: 'member'; id: string }
  /** A line on the code map: `call:${from}->${to}`, `uses:...` or `inh:...` (see code/graph.ts). */
  | { kind: 'edge'; id: string };

export interface CodeMapState {
  map: CodeMap | null;
  /** Files read so far while loading, for the progress message. */
  loading: { read: number; total: number } | null;
  error: string | null;
  /** The code tab in front, if any (the inspector shows what is selected on it). */
  view: CodeView | null;
  selection: CodeSelection | null;
  /** The endpoint the endpoint map shows. */
  endpoint: string | null;
  /** The test the test map shows (a test method's member id). */
  test: string | null;
  /** A request to bring a file or member into view (or focus on its file) in one view; a new nonce repeats it. */
  reveal: { view: CodeView; path: string; member?: string; nonce: number; focus?: boolean } | null;
}

export const useCodeMap = create<CodeMapState>(() => ({ map: null, loading: null, error: null, view: null, selection: null, endpoint: null, test: null, reveal: null }));

/** File texts already read, so a change reads only the files that changed. */
const texts = new Map<string, string>();
let running: Promise<void> | null = null;
let again = false;
let sourceId: string | null = null;

/** The C# files of the open folder the code map reads. */
export function mappedFiles(): string[] {
  return useWorkspace
    .getState()
    .files.map((f) => f.path)
    .filter(isMappedPath);
}

/** Project files: `.csproj` and `Directory.Build.props`, outside hidden folders and build output. */
function isProjectFile(path: string): boolean {
  const name = path.slice(path.lastIndexOf('/') + 1);
  return (name.endsWith('.csproj') || name === 'Directory.Build.props') && !path.split('/').some((s) => s.startsWith('.') || s === 'bin' || s === 'obj' || s === 'node_modules');
}

/** Builds the code map (or builds it again), reading only files not read before. */
export function loadCodeMap(): Promise<void> {
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    do {
      again = false;
      const { source, files } = useWorkspace.getState();
      if (!source) return;
      if (sourceId !== source.id) {
        texts.clear();
        sourceId = source.id;
      }
      const paths = mappedFiles();
      useCodeMap.setState({ loading: { read: 0, total: paths.length }, error: null });
      const read: { path: string; text: string }[] = [];
      for (let i = 0; i < paths.length; i += 24) {
        const batch = paths.slice(i, i + 24);
        const loaded = await Promise.all(
          batch.map(async (path) => {
            let text = texts.get(path);
            if (text === undefined) {
              text = await source.read(path).catch(() => '');
              texts.set(path, text);
            }
            return { path, text };
          }),
        );
        for (const f of loaded) if (!isGeneratedCode(f.path, f.text)) read.push(f);
        useCodeMap.setState({ loading: { read: Math.min(paths.length, i + batch.length), total: paths.length } });
      }
      const projectFiles = await Promise.all(
        files
          .map((f) => f.path)
          .filter(isProjectFile)
          .map(async (path) => {
            let text = texts.get(path);
            if (text === undefined) {
              text = await source.read(path).catch(() => '');
              texts.set(path, text);
            }
            return { path, text };
          }),
      );
      // The source may have changed while files were being read.
      if (useWorkspace.getState().source !== source) continue;
      const projects = projectFiles.filter((f) => f.path.endsWith('.csproj'));
      const props = projectFiles.filter((f) => !f.path.endsWith('.csproj'));
      const map = await parser().codeMap(read, projects, props);
      if (useWorkspace.getState().source !== source) continue;
      useCodeMap.setState({ map, loading: null });
      // Watch the folders of the files on the map, so edits show up live (at most a few hundred).
      setExtraWatchFolders([...new Set(read.map((f) => (f.path.includes('/') ? f.path.slice(0, f.path.lastIndexOf('/')) : '')))].slice(0, 400));
    } while (again);
  })()
    .catch((e: Error) => useCodeMap.setState({ loading: null, error: `Could not read the code: ${e.message}` }))
    .finally(() => {
      running = null;
    });
  return running;
}

export function selectCode(selection: CodeSelection | null): void {
  useCodeMap.setState({ selection });
}

/** Brings a file (or a member's file) into view on the code tab in front, or focuses on it. */
export function revealCode(path: string, options: { member?: string; focus?: boolean } = {}): void {
  useCodeMap.setState((s) => ({
    selection: options.member ? { kind: 'member', id: options.member } : { kind: 'file', path },
    reveal: { view: s.view ?? 'code', path, member: options.member, nonce: Date.now(), focus: options.focus },
  }));
}

/** Shows an endpoint on the endpoint map. */
export function showEndpoint(id: string): void {
  useCodeMap.setState({ endpoint: id, selection: null });
}

/** Shows a test on the test map. */
export function showTest(id: string): void {
  useCodeMap.setState({ test: id, selection: null });
}

// A new folder starts empty; a change to C# files on the map reads them again.
useWorkspace.subscribe((s, prev) => {
  if (s.source !== prev.source) {
    texts.clear();
    sourceId = s.source?.id ?? null;
    useCodeMap.setState({ map: null, loading: null, error: null, selection: null, endpoint: null, test: null, reveal: null });
    return;
  }
  if (s.lastChange && s.lastChange !== prev.lastChange && useCodeMap.getState().map) {
    const touched = s.lastChange.paths.filter((p) => p.endsWith('.cs') || isProjectFile(p));
    const filesChanged = s.files !== prev.files;
    if (!touched.length && !filesChanged) return;
    for (const p of touched) texts.delete(p);
    void loadCodeMap();
  }
});
